import React, { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Instance } from 'tippy.js';

import type { LatestMatch } from 'modules/main/domain/interfaces/latest-match';
import { formatMatchTime } from 'modules/main/domain/utils/format-match-time';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import MatchPreview from './match-preview';

interface NewsTickerItemProps {
  match: LatestMatch;
  /** Копия, доступная скринридеру и табуляции; остальные копии ленты только повторяют её. */
  isAccessible: boolean;
}

/** Сколько курсор должен простоять на месте, чтобы показалась подсказка с полным текстом, мс. */
const TOOLTIP_DELAY_MS = 1000;

/**
 * Запись бегущей строки: превью с эмблемами («A vs B») и счётом под каждой, время матча и названия команд в две строки
 * с многоточием. Название ссылки для скринридера — `match.title` («А 4:2 Б»): счёт в видимом тексте стоит отдельно.
 * Ведёт на страницу матча (или его турнира) внутри админки.
 *
 * Полный текст — в подсказке, но не по самому наведению (иначе она выскакивала бы на каждую запись, пока
 * курсор едет через ленту), а когда курсор **простоял** на записи `TOOLTIP_DELAY_MS`: любое движение
 * сбрасывает отсчёт и прячет уже показанную подсказку. Поэтому тригер `manual` и таймер здесь, а не
 * штатная задержка tippy — та считается только от входа курсора и движение не учитывает.
 * С клавиатуры подсказка показывается сразу по фокусу.
 */
function NewsTickerItem({ match, isAccessible }: NewsTickerItemProps) {
  const instanceRef = useRef<Instance | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearTimer = useCallback(() => clearTimeout(timerRef.current), []);

  const hide = useCallback(() => {
    clearTimer();
    instanceRef.current?.hide();
  }, [clearTimer]);

  const armTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => instanceRef.current?.show(), TOOLTIP_DELAY_MS);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return (
    <UDTippy
      content={match.description}
      placement="top"
      size="md"
      trigger="manual"
      onCreate={(instance) => {
        instanceRef.current = instance;
      }}
    >
      <Link
        to={match.path}
        tabIndex={isAccessible ? undefined : -1}
        aria-label={match.title}
        className="rounded-card hover:bg-accentCornsilk flex items-center gap-3 px-2.5 py-2 transition-colors"
        onMouseEnter={armTimer}
        onMouseMove={() => {
          instanceRef.current?.hide();
          armTimer();
        }}
        onMouseLeave={hide}
        onClick={hide}
        onFocus={() => {
          clearTimer();
          instanceRef.current?.show();
        }}
        onBlur={hide}
      >
        <MatchPreview teams={match.teams} />
        <span className="flex w-48 flex-col gap-1 whitespace-normal">
          {match.playedAt !== null && (
            <UDText variant="subtitle-2" as="span" className="text-inkMuted">
              {formatMatchTime(match.playedAt)}
            </UDText>
          )}
          <UDText variant="text-2-medium" as="span" relaxed className="text-inkPrimary line-clamp-2">
            {match.teams[0].name} — {match.teams[1].name}
          </UDText>
        </span>
      </Link>
    </UDTippy>
  );
}

export default NewsTickerItem;
